import React, { useState, useRef, useEffect } from 'react';
import { X, Play, Square, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { batchGenerateSideGuides, type BatchSideGuideMessage } from '../../lib/api';

type PlanVersion = {
  id: number;
  version_number: string;
  created_by_username: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  planId: number;
  lessonInfo?: {
    id: number;
    title: string;
    sort_order: number;
  };
  stageInfo?: {
    title: string;
  };
  plans: PlanVersion[];
  onSlideStatusChange: () => void;
};

type LogMessage = {
  id: number;
  type: 'info' | 'success' | 'error' | 'warning' | 'progress';
  message: string;
  timestamp: Date;
};

export const BatchSideGuideModal: React.FC<Props> = ({
  isOpen,
  onClose,
  planId,
  lessonInfo,
  stageInfo,
  plans,
  onSlideStatusChange
}) => {
  const [startFromIndex, setStartFromIndex] = useState<number>(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [logs, setLogs] = useState<LogMessage[]>([]);
  const [wsConnectionStatus, setWsConnectionStatus] = useState<'not_connected' | 'connecting' | 'connected' | 'disconnected'>('not_connected');
  const [progress, setProgress] = useState<{
    total: number;
    current: number;
    success: number;
    error: number;
    skipped: number;
  } | null>(null);
  
  const wsRef = useRef<ReturnType<typeof batchGenerateSideGuides> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const logIdRef = useRef(0);

  const addLog = (type: LogMessage['type'], message: string) => {
    logIdRef.current += 1;
    setLogs(prev => [...prev, {
      id: logIdRef.current,
      type,
      message,
      timestamp: new Date()
    }]);
  };

  useEffect(() => {
    if (isOpen) {
      setLogs([]);
      setProgress(null);
      setWsConnectionStatus('connecting');
      
      try {
        const handleMessage = (msg: BatchSideGuideMessage) => {
          console.log('📩 [BatchSideGuide] Message:', msg);
          
          switch (msg.type) {
            case 'start':
              setProgress({
                total: msg.total_slides || 0,
                current: 0,
                success: 0,
                error: 0,
                skipped: msg.skipped_count || 0
              });
              addLog('info', msg.message || `开始批量生成，共 ${msg.total_slides} 个slide${msg.start_from_index ? `，从第 ${msg.start_from_index} 页开始` : ''}${msg.skipped_count ? `，跳过 ${msg.skipped_count} 个` : ''}`);
              break;
              
            case 'slide_start':
              addLog('progress', `正在生成第 ${msg.current_slide_index} 页教案...`);
              if (progress) {
                setProgress(prev => prev ? { ...prev, current: msg.current_slide_index || prev.current } : null);
              }
              break;
              
            case 'slide_done':
              addLog('success', `第 ${msg.current_slide_index} 页教案生成完成`);
              break;
              
            case 'progress':
              if (msg.message) {
                addLog('info', msg.message);
              }
              break;
              
            case 'completed':
              setIsGenerating(false);
              addLog('success', msg.message || `批量生成完成！成功 ${msg.success_count} 个，失败 ${msg.error_count} 个`);
              if (progress) {
                setProgress(prev => prev ? {
                  ...prev,
                  success: msg.success_count || 0,
                  error: msg.error_count || 0
                } : null);
              }
              onSlideStatusChange();
              break;
              
            case 'error':
              addLog('error', msg.content || msg.message || '发生错误');
              if (msg.content === 'WebSocket connection error' || msg.content === 'WebSocket closed unexpectedly') {
                setWsConnectionStatus('disconnected');
                setIsGenerating(false);
              }
              break;
          }
        };
        
        const handleOpen = () => {
          setWsConnectionStatus('connected');
          addLog('info', 'WebSocket已连接，等待发送开始指令');
        };
        
        wsRef.current = batchGenerateSideGuides(planId, handleMessage, handleOpen);
        
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
        setWsConnectionStatus('disconnected');
        addLog('error', 'WebSocket连接失败');
      }
    } else {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setWsConnectionStatus('not_connected');
      setIsGenerating(false);
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isOpen, planId]);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isOpen]);

  const handleStartGeneration = () => {
    if (!wsRef.current || wsRef.current.getReadyState() !== WebSocket.OPEN) {
      addLog('error', 'WebSocket未连接，请稍后重试');
      return;
    }
    
    setIsGenerating(true);
    addLog('info', `发送开始生成指令，从第 ${startFromIndex} 页开始`);
    wsRef.current.start(startFromIndex);
  };

  const handleClose = () => {
    if (isGenerating) {
      if (!confirm('正在生成中，确定要关闭吗？')) {
        return;
      }
    }
    onClose();
  };

  if (!isOpen) return null;

  const selectedPlan = plans.find(p => p.id === planId);

  const getLogIcon = (type: LogMessage['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle size={16} className="text-green-500 flex-shrink-0" />;
      case 'error':
        return <XCircle size={16} className="text-red-500 flex-shrink-0" />;
      case 'warning':
        return <AlertCircle size={16} className="text-yellow-500 flex-shrink-0" />;
      case 'progress':
        return <Loader2 size={16} className="text-blue-500 flex-shrink-0 animate-spin" />;
      default:
        return <div className="w-4 h-4 rounded-full bg-gray-300 flex-shrink-0" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[80vw] max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">批量生成每页教案</h3>
            <p className="text-sm text-gray-500 mt-1">
              {stageInfo?.title} - 第 {lessonInfo?.sort_order} 课 - {selectedPlan?.version_number || planId} 版教案
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${
                wsConnectionStatus === 'connected' ? 'bg-green-500' :
                wsConnectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                wsConnectionStatus === 'disconnected' ? 'bg-red-500' : 'bg-gray-400'
              }`} />
              <span className="text-sm text-gray-600">
                {wsConnectionStatus === 'connected' ? '已连接' :
                 wsConnectionStatus === 'connecting' ? '连接中...' :
                 wsConnectionStatus === 'disconnected' ? '已断开' : '未连接'}
              </span>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-end gap-4">
            <div className="flex-1 max-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                开始位置 (slide_index)
              </label>
              <input
                type="number"
                min="1"
                value={startFromIndex}
                onChange={(e) => setStartFromIndex(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="从第几页开始"
                disabled={isGenerating}
              />
              <p className="text-xs text-gray-500 mt-1">
                传1=从第1页开始，传5=从第5页开始
              </p>
            </div>
            
            <button
              onClick={handleStartGeneration}
              disabled={isGenerating || wsConnectionStatus !== 'connected'}
              className={`px-6 py-2 rounded-md font-medium flex items-center gap-2 transition-colors ${
                isGenerating || wsConnectionStatus !== 'connected'
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {isGenerating ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  生成中...
                </>
              ) : (
                <>
                  <Play size={16} />
                  开始生成
                </>
              )}
            </button>
          </div>
        </div>

        {progress && (
          <div className="px-4 py-3 bg-purple-50 border-b border-purple-100">
            <div className="flex items-center justify-between text-sm">
              <span className="text-purple-700 font-medium">
                进度: {progress.current} / {progress.total}
              </span>
              <div className="flex items-center gap-4 text-xs">
                {progress.skipped > 0 && (
                  <span className="text-gray-500">跳过: {progress.skipped}</span>
                )}
                <span className="text-green-600">成功: {progress.success}</span>
                {progress.error > 0 && (
                  <span className="text-red-600">失败: {progress.error}</span>
                )}
              </div>
            </div>
            <div className="mt-2 h-2 bg-purple-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-purple-600 transition-all duration-300"
                style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto p-4 bg-gray-50">
          {logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <AlertCircle size={48} className="mb-2" />
              <p>点击"开始生成"按钮开始批量生成教案</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => (
                <div 
                  key={log.id}
                  className={`flex items-start gap-2 p-2 rounded-md ${
                    log.type === 'error' ? 'bg-red-50' :
                    log.type === 'success' ? 'bg-green-50' :
                    log.type === 'warning' ? 'bg-yellow-50' :
                    log.type === 'progress' ? 'bg-blue-50' : 'bg-white'
                  }`}
                >
                  {getLogIcon(log.type)}
                  <span className={`text-sm ${
                    log.type === 'error' ? 'text-red-700' :
                    log.type === 'success' ? 'text-green-700' :
                    log.type === 'warning' ? 'text-yellow-700' :
                    log.type === 'progress' ? 'text-blue-700' : 'text-gray-700'
                  }`}>
                    {log.message}
                  </span>
                  <span className="text-xs text-gray-400 ml-auto flex-shrink-0">
                    {log.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors text-sm"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};
